import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { useApolloClient, useQuery } from '@apollo/client';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { omit, pick } from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { useNotification } from '../../../../components/Notification';
import {
  CREATE_RECEIVE_EXCEPTION,
  GET_RECEIVE_EXCEPTION,
  UPDATE_RECEIVE_EXCEPTION,
} from '../../../../services/receive-exception/queries';
import {
  ReceiveException,
  RECEIVE_EXCEPTION_ASSIGNEE_TYPE,
} from '../../../../services/receive-exception/types';
import { mapEmptyStringParametersToNull } from '../../../../utils';
import { SELECT_EMPTY_VALUE, updatableFormFields } from '../config';
import {
  getCreateFailureNotification,
  getCreateSuccessNotification,
  getUpdateFailureNotification,
  getUpdateSuccessNotification,
  transformExceptionForForm,
  transformReceiveExceptionError,
} from '../helper';
import {
  ExceptionFormSchema,
  ExceptionFormState,
  ExceptionFormValues,
  InitialValues,
} from './schema';
import {
  getAutoFilledFormValuesFromUrlParameters,
} from "./autoFillExceptionParameters";
import { useQueryAppConfig } from '../appConfigUtils';

export function useExceptionForm(exceptionId?: string): ExceptionFormState {
  const history = useHistory();
  const client = useApolloClient();
  const { addNotification, clearByGroup } = useNotification();
  const [receiveException, setReceiveException] = useState<ReceiveException>();
  const [fetchError, setFetchError] = useState<string>();
  const [assignedToGroups, setAssignedToGroups] = useState<OptionDefinition[]>([]);

  const updateException = async (values: ExceptionFormValues) => {
    try {
      clearByGroup('receive-exception-update');
      const castedValue = mapEmptyStringParametersToNull(ExceptionFormSchema.cast(values));

      // Default the assignedToType to RESOLVER_GROUP till we implement the other Assignee types.
      castedValue.assignedToType = RECEIVE_EXCEPTION_ASSIGNEE_TYPE.RESOLVER_GROUP;

      // remove form state fields and pick only updatable fields
      const request = pick(
        omit(castedValue, ['serial', 'tag', 'entireOrder']),
        updatableFormFields
      );
      await client.mutate({
        mutation: UPDATE_RECEIVE_EXCEPTION,
        variables: { request: { ...request, exceptionId } },
      });
      history.push(`/receive/exception/${exceptionId}`);
      addNotification(getUpdateSuccessNotification());
    } catch (e) {
      addNotification(getUpdateFailureNotification(e));
    }
  };

  const createException = async (
    values: ExceptionFormValues,
    formHelpers: FormikHelpers<ExceptionFormValues>
  ) => {
    clearByGroup('receive-exception-create');
    // remove form state fields from request
    const valuesToSubmit = omit(values, ['serial', 'tag', 'entireOrder']);

    const request = mapEmptyStringParametersToNull(ExceptionFormSchema.cast(valuesToSubmit));

    // Default the assignedToType to RESOLVER_GROUP till we implement the other Assignee types.
    request.assignedToType = RECEIVE_EXCEPTION_ASSIGNEE_TYPE.RESOLVER_GROUP;

    try {
      const result = await client.mutate({
        mutation: CREATE_RECEIVE_EXCEPTION,
        variables: { request },
      });
      formHelpers.resetForm();
      addNotification(
        getCreateSuccessNotification(result.data.createReceiveException, history, clearByGroup)
      );
    } catch (e) {
      addNotification(getCreateFailureNotification(e));
    }
  };
  const form = useFormik<ExceptionFormValues>({
    initialValues: InitialValues,
    validationSchema: ExceptionFormSchema,
    validateOnChange: false,
    validateOnBlur: true,
    validate: (values) => {
      const errors: FormikErrors<ExceptionFormValues> = {};

      if (!values.orderLineNumber && !values.entireOrder) {
        errors.orderLineNumber = 'Provide either order line number or select entire order';
      }

      return Promise.resolve(errors);
    },
    onSubmit: async (values: ExceptionFormValues, helpers: FormikHelpers<ExceptionFormValues>) => {
      if (exceptionId) {
        return updateException(values);
      }
      return createException(values, helpers);
    },
  });

  // Fetch exception type and resolver group info from AWS AppConfig
  const {
    resolverGroupOptionDefinitions,
    fetchConfigError,
    exceptionTypes,
    loadingResolverConfig,
  } = useQueryAppConfig(!!exceptionId);
  
  const { loading: fetchingException } = useQuery(GET_RECEIVE_EXCEPTION, {
    variables: { exceptionId },
    skip: !exceptionId || exceptionTypes === [],
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      setReceiveException(result.getReceiveException);
      form.setValues(transformExceptionForForm(result.getReceiveException, exceptionTypes));
    },
    onError: (error) => {
      setFetchError(transformReceiveExceptionError(error));
    },
  });
  
  useEffect(() => {
    // Not ideal way to do this but !exceptionId basically means make sure we're not called by the edit form
    if (!exceptionId && !loadingResolverConfig && exceptionTypes) {
      // Gets here only once when loaded and exceptionTypes exist
      // Sets the exception type in the url parameter if defined
      const autoFilledValues = getAutoFilledFormValuesFromUrlParameters(exceptionTypes);
      for (let k of Object.keys(autoFilledValues)) {
        form.setFieldValue(k, autoFilledValues[k], true);
  
        if (k === 'serial') {
          // automatically add serial to serials if it is valid
          if (yup.reach(ExceptionFormSchema, 'serial').isValidSync(autoFilledValues[k])) {
            form.setFieldValue('serials', [{label: autoFilledValues[k]}], true);
            form.setFieldValue('serial', '');
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingResolverConfig]);
  
  useEffect(() => {
    if (form.values.exceptionType?.value && resolverGroupOptionDefinitions) {
      const assignedToGroupOptions = resolverGroupOptionDefinitions.get(
        form.values.exceptionType.value as string
      );

      if (assignedToGroupOptions) {
        setAssignedToGroups([SELECT_EMPTY_VALUE, ...assignedToGroupOptions]);

        // auto select if there is only one value
        if (assignedToGroupOptions.length === 1) {
          form.setFieldValue('assignedToId', assignedToGroupOptions[0], true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.exceptionType?.value]);

  return {
    form,
    receiveException,
    fetchingException,
    fetchError,
    assignedToGroups,
    exceptionTypes,
    loadingResolverConfig,
    fetchConfigError,
  };
}
